'use client';

import { useTaxAndAccountingOnboarding } from '@commenda/api';
import { LoadingIndicator, SingleCombobox } from '@commenda/kakashi';
import { Country } from '@commenda/prisma';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { LabelWithGuidanceTooltip } from '../../../form-builder/components/wrappers/LabelWithGuidanceTooltip';
import { NumberInputComponent } from '../../../form-builder/components/wrappers/NumberInput';
import { RadioGroupComponent } from '../../../form-builder/components/wrappers/RadioGroup';
import { SelectComponent } from '../../../form-builder/components/wrappers/Select';
import { FormContext } from '../../../form-builder/hooks/FormContext';
import { getProfileFields } from './countryConfig';
import { PROFILE_FIELD_CATALOG } from './fieldCatalog';
import {
  ProfileFieldDefinition,
  ProfileFieldKey,
} from './fieldCatalog.types';
import { seedProfileFormData } from './wizardLogic';
import { WizardModuleCard } from './WizardModuleCard';

export const Part2Profile = ({
  corporationId,
  fiscalYear,
  country,
}: {
  corporationId: number;
  fiscalYear: number;
  country: Country | undefined;
}) => {
  const { formData, setFormData, updateField } = useContext(FormContext);
  const {
    data: profile,
    isLoading,
    error,
  } = useTaxAndAccountingOnboarding(corporationId, fiscalYear);

  const [prefilledFrom, setPrefilledFrom] = useState<
    Partial<Record<ProfileFieldKey, number>>
  >({});
  const prefillSeed = useRef<Record<string, unknown>>({});
  const seededFy = useRef<number | null>(null);

  // Seed once per fiscal year: the FY's own answers, then carry-forward pre-fill for the gaps.
  useEffect(() => {
    if (isLoading) return;
    if (seededFy.current === fiscalYear) return;
    const { seed, prefilledFrom: sources } = seedProfileFormData(
      profile,
      country
    );

    seededFy.current = fiscalYear;
    prefillSeed.current = seed;
    setPrefilledFrom(sources);
    setFormData((prev) => ({ ...seed, ...prev }));
  }, [profile, isLoading, fiscalYear, country, setFormData]);

  const hint = (key: ProfileFieldKey): ReactNode => {
    const fy = prefilledFrom[key];
    if (fy === undefined) return null;
    // Hide once the user has changed the pre-filled value.
    if (formData[key] !== prefillSeed.current[key]) return null;
    return (
      <p className="-mt-2 mb-4 txt-small-regular text-text-tertiary">
        Pre-filled from FY {fy}
      </p>
    );
  };

  const renderField = (entry: ProfileFieldDefinition): ReactNode => {
    switch (entry.inputKind) {
      case 'number':
        return (
          <div key={entry.key}>
            <NumberInputComponent
              fieldId={entry.key}
              label={entry.label}
              validation={{ rules: { required: true } }}
            />
            {hint(entry.key)}
          </div>
        );
      case 'select':
        return (
          <div key={entry.key}>
            <SelectComponent
              fieldId={entry.key}
              label={entry.label}
              placeholder={entry.placeholder}
              options={entry.options ?? []}
              validation={{ rules: { required: true } }}
            />
            {hint(entry.key)}
          </div>
        );
      case 'radio':
        return (
          <div key={entry.key}>
            <RadioGroupComponent
              fieldId={entry.key}
              label={entry.label}
              options={entry.options ?? []}
              props={{ radioLayout: 'horizontal' }}
              validation={{ rules: { required: true } }}
            />
            {hint(entry.key)}
          </div>
        );
      case 'combobox':
        return (
          <div className="mb-4" key={entry.key}>
            <LabelWithGuidanceTooltip
              label={entry.label}
              description={entry.guidance}
            />
            <SingleCombobox
              options={(entry.options ?? []).map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              value={(formData[entry.key] as string) ?? ''}
              setValue={(value) => updateField(entry.key, value || undefined)}
              placeholder={entry.placeholder}
              canClearSelection
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <LoadingIndicator size={24} />
      </div>
    );
  }

  // Surface a load failure rather than falling through to a blank form — otherwise the user can't
  // tell their saved answers failed to load and could overwrite them by submitting the empty form.
  if (error) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-1 text-center">
        <p className="txt-body-medium text-text-primary">
          We couldn&apos;t load your saved answers.
        </p>
        <p className="txt-small-regular text-text-tertiary">
          Please close and reopen this step, or contact Team Commenda if it keeps
          happening.
        </p>
      </div>
    );
  }

  return (
    <WizardModuleCard
      title="Tax & Accounting profile"
      description="We use these answers to determine which filings apply to your entity for this fiscal year."
    >
      {getProfileFields(country).map((key) =>
        renderField(PROFILE_FIELD_CATALOG[key])
      )}
    </WizardModuleCard>
  );
};
